'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MortgageAmortizationCalculator() {
  const t = useTranslations("tools.mortgageAmortizationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    homePrice: 500000,
    downPayment: 20,
    loanTerm: 30,
    interestRate: 6.286,
    propertyTax: 1.2,
    homeInsurance: 2500,
    pmi: 0,
    hoaFee: 0,
    otherCosts: 5000,
    startMonth: 11,
    startYear: 2025,
    includeOptional: true
  });

  const [results, setResults] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const homePrice = inputs.homePrice;
    const downPaymentPct = inputs.downPayment;
    const loanTermYears = inputs.loanTerm;
    const interestRate = inputs.interestRate;
    const startMonth = inputs.startMonth;
    const startYear = inputs.startYear;

    const includeOptional = inputs.includeOptional;
    const propertyTaxPct = includeOptional ? inputs.propertyTax : 0;
    const homeInsurance = includeOptional ? inputs.homeInsurance : 0;
    const pmi = includeOptional ? inputs.pmi : 0;
    const hoaFee = includeOptional ? inputs.hoaFee : 0;
    const otherCosts = includeOptional ? inputs.otherCosts : 0;

    const downPaymentAmount = homePrice * (downPaymentPct / 100);
    const loanAmount = homePrice - downPaymentAmount;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTermYears * 12;

    const monthlyPI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);

    const propertyTaxMonthly = (homePrice * propertyTaxPct / 100) / 12;
    const homeInsMonthly = homeInsurance / 12;
    const pmiMonthly = pmi / 12;
    const hoaMonthly = hoaFee / 12;
    const otherMonthly = otherCosts / 12;

    const totalMonthlyPayment = monthlyPI + propertyTaxMonthly + homeInsMonthly + pmiMonthly + hoaMonthly + otherMonthly;

    let balance = loanAmount;
    let totalInterest = 0;
    const scheduleData = [];

    for (let i = 1; i <= numPayments; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPI - interestPayment;
      balance -= principalPayment;
      totalInterest += interestPayment;

      const paymentDate = new Date(startYear, startMonth + i, 1);

      scheduleData.push({
        date: paymentDate,
        payment: monthlyPI,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
    }

    const payoffDate = new Date(startYear, startMonth + numPayments, 1);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const payoffDateStr = monthNames[payoffDate.getMonth()] + ' ' + payoffDate.getFullYear();

    setResults({
      monthlyPI,
      propertyTaxMonthly,
      homeInsMonthly,
      pmiMonthly,
      hoaMonthly,
      otherMonthly,
      totalMonthlyPayment,
      homePrice,
      loanAmount,
      downPaymentAmount,
      totalInterest,
      totalPayments: monthlyPI * numPayments,
      payoffDate: payoffDateStr,
      includeOptional
    });

    setSchedule(scheduleData);
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem'
    },
    toolTitle: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorLayout: getResponsiveStyle(
      { display: 'grid', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formSection: {
      marginBottom: '1.5rem'
    },
    formSectionTitle: getResponsiveStyle(
      { color: '#2563eb', marginBottom: '1rem', fontWeight: 600 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.1rem' }
    ),
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    inputWrapper: {
      position: 'relative' as const
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 500
    },
    inputSuffix: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 500
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        boxSizing: 'border-box' as const,
        background: 'white'
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' }
    ),
    checkboxGroup: getResponsiveStyle(
      {
        display: 'flex',
        alignItems: 'center',
        margin: '1.5rem 0',
        background: '#f8fafc',
        borderRadius: '0.5rem'
      },
      { padding: '0.75rem' },
      { padding: '1rem' },
      { padding: '1rem' }
    ),
    checkbox: {
      width: '1.25rem',
      height: '1.25rem',
      marginRight: '0.75rem',
      cursor: 'pointer'
    },
    checkboxLabel: {
      margin: 0,
      cursor: 'pointer',
      color: colors.textPrimary,
      fontSize: '0.95rem'
    },
    dateInputs: getResponsiveStyle(
      { display: 'grid', gap: '0.75rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '2fr 1fr' },
      { gridTemplateColumns: '2fr 1fr' }
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const, fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    monthlyPayment: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem'
      },
      { padding: '1.25rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    monthlyPaymentLabel: {
      fontSize: '0.9rem',
      opacity: 0.9,
      marginBottom: '0.5rem'
    },
    monthlyPaymentValue: getResponsiveStyle(
      { fontWeight: 700 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.5rem' }
    ),
    summaryTable: {
      width: '100%',
      marginBottom: '1.5rem'
    },
    summaryRow: {
      borderBottom: '1px solid #e2e8f0'
    },
    summaryCell: getResponsiveStyle(
      { color: '#64748b' },
      { padding: '0.6rem 0.25rem', fontSize: '0.85rem' },
      { padding: '0.75rem 0.5rem', fontSize: '0.95rem' },
      { padding: '0.75rem 0.5rem', fontSize: '0.95rem' }
    ),
    summaryCellValue: getResponsiveStyle(
      { textAlign: 'right' as const, fontWeight: 600, color: colors.textPrimary },
      { padding: '0.6rem 0.25rem', fontSize: '0.85rem' },
      { padding: '0.75rem 0.5rem', fontSize: '0.95rem' },
      { padding: '0.75rem 0.5rem', fontSize: '0.95rem' }
    ),
    highlightRow: {
      background: '#f8fafc',
      borderBottom: 'none'
    },
    highlightCell: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb' },
      { padding: '0.75rem 0.25rem', fontSize: '0.9rem' },
      { padding: '1rem 0.5rem', fontSize: '0.95rem' },
      { padding: '1rem 0.5rem', fontSize: '0.95rem' }
    ),
    amortizationSchedule: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        marginTop: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    scheduleTitle: getResponsiveStyle(
      { color: colors.textPrimary, marginBottom: '1.5rem', fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    scheduleTableWrapper: {
      overflowX: 'auto' as const,
      maxHeight: '500px',
      overflowY: 'auto' as const
    },
    scheduleTable: getResponsiveStyle(
      { width: '100%', borderCollapse: 'collapse' as const },
      { fontSize: '0.8rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    scheduleHeader: {
      position: 'sticky' as const,
      top: 0,
      background: '#f8fafc',
      zIndex: 10
    },
    scheduleTh: getResponsiveStyle(
      { textAlign: 'left' as const, fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' },
      { padding: '0.75rem 0.5rem' },
      { padding: '1rem 0.75rem' },
      { padding: '1rem 0.75rem' }
    ),
    scheduleTd: getResponsiveStyle(
      { borderBottom: '1px solid #f1f5f9' },
      { padding: '0.6rem 0.5rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    scheduleRow: {
      transition: 'background 0.2s'
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    listItem: {
      marginBottom: '0.5rem'
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    referenceList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    referenceItem: {
      marginBottom: '0.75rem'
    },
    referenceLink: getResponsiveStyle(
      { color: '#2563eb', textDecoration: 'none', transition: 'color 0.2s' },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    )
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.toolTitle}>🏠 {t("toolTitle")}</h2>
        </div>

        <div style={styles.calculatorLayout}>
          <div style={styles.calculatorForm}>
            <form onSubmit={handleCalculate}>
              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>{t("loanDetailsTitle")}</h3>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="homePrice">{t("homePriceLabel")}</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="homePrice"
                      style={{ ...styles.input, paddingLeft: '2.5rem' }}
                      value={inputs.homePrice}
                      onChange={(e) => setInputs({...inputs, homePrice: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                      required
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="downPayment">{t("downPaymentLabel")}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="downPayment"
                      style={{ ...styles.input, paddingRight: '3rem' }}
                      value={inputs.downPayment}
                      onChange={(e) => setInputs({...inputs, downPayment: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                    <span style={styles.inputSuffix}>%</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="loanTerm">{t("loanTermLabel")}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="loanTerm"
                      style={{ ...styles.input, paddingRight: '3rem' }}
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({...inputs, loanTerm: parseInt(e.target.value) || 0})}
                      min="1"
                      max="50"
                      required
                    />
                    <span style={styles.inputSuffix}>{t("yearsUnit")}</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="interestRate">{t("interestRateLabel")}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="interestRate"
                      style={{ ...styles.input, paddingRight: '3rem' }}
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="20"
                      step="0.001"
                      required
                    />
                    <span style={styles.inputSuffix}>%</span>
                  </div>
                </div>
              </div>

              <div style={styles.checkboxGroup}>
                <input
                  type="checkbox"
                  id="includeOptional"
                  style={styles.checkbox}
                  checked={inputs.includeOptional}
                  onChange={(e) => setInputs({...inputs, includeOptional: e.target.checked})}
                />
                <label style={styles.checkboxLabel} htmlFor="includeOptional">
                  <strong>{t("includeOptionalCosts")}</strong>
                </label>
              </div>

              {inputs.includeOptional && (
                <div style={styles.formSection}>
                  <h3 style={styles.formSectionTitle}>{t("optionalCostsTitle")}</h3>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="propertyTax">{t("propertyTaxLabel")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="propertyTax"
                        style={{ ...styles.input, paddingRight: '3rem' }}
                        value={inputs.propertyTax}
                        onChange={(e) => setInputs({...inputs, propertyTax: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="0.1"
                      />
                      <span style={styles.inputSuffix}>%</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="homeInsurance">{t("homeInsuranceLabel")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="homeInsurance"
                        style={{ ...styles.input, paddingLeft: '2.5rem', paddingRight: '3.5rem' }}
                        value={inputs.homeInsurance}
                        onChange={(e) => setInputs({...inputs, homeInsurance: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                      />
                      <span style={styles.inputSuffix}>{t("perYearUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="pmi">{t("pmiLabel")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="pmi"
                        style={{ ...styles.input, paddingLeft: '2.5rem', paddingRight: '3.5rem' }}
                        value={inputs.pmi}
                        onChange={(e) => setInputs({...inputs, pmi: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                      />
                      <span style={styles.inputSuffix}>{t("perYearUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="hoaFee">{t("hoaFeeLabel")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="hoaFee"
                        style={{ ...styles.input, paddingLeft: '2.5rem', paddingRight: '3.5rem' }}
                        value={inputs.hoaFee}
                        onChange={(e) => setInputs({...inputs, hoaFee: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                      />
                      <span style={styles.inputSuffix}>{t("perYearUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="otherCosts">{t("otherCostsLabel")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="otherCosts"
                        style={{ ...styles.input, paddingLeft: '2.5rem', paddingRight: '3.5rem' }}
                        value={inputs.otherCosts}
                        onChange={(e) => setInputs({...inputs, otherCosts: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                      />
                      <span style={styles.inputSuffix}>{t("perYearUnit")}</span>
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>{t("startDateTitle")}</h3>
                <div style={styles.formGroup}>
                  <div style={styles.dateInputs}>
                    <select
                      id="startMonth"
                      style={styles.select}
                      value={inputs.startMonth}
                      onChange={(e) => setInputs({...inputs, startMonth: parseInt(e.target.value)})}
                    >
                      <option value="0">{t("monthJanuary")}</option>
                      <option value="1">{t("monthFebruary")}</option>
                      <option value="2">{t("monthMarch")}</option>
                      <option value="3">{t("monthApril")}</option>
                      <option value="4">{t("monthMay")}</option>
                      <option value="5">{t("monthJune")}</option>
                      <option value="6">{t("monthJuly")}</option>
                      <option value="7">{t("monthAugust")}</option>
                      <option value="8">{t("monthSeptember")}</option>
                      <option value="9">{t("monthOctober")}</option>
                      <option value="10">{t("monthNovember")}</option>
                      <option value="11">{t("monthDecember")}</option>
                    </select>
                    <input
                      type="number"
                      id="startYear"
                      style={styles.input}
                      value={inputs.startYear}
                      onChange={(e) => setInputs({...inputs, startYear: parseInt(e.target.value) || 2025})}
                      min="2000"
                      max="2100"
                      required
                    />
                  </div>
                </div>
              </div>

              <button type="submit" style={styles.calculateButton}>
                {t("calculateButton")}
              </button>
            </form>
          </div>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("paymentSummaryTitle")}</h3>

              <div style={styles.monthlyPayment}>
                <div style={styles.monthlyPaymentLabel}>{t("monthlyPaymentLabel")}</div>
                <div style={styles.monthlyPaymentValue}>{formatCurrency(results.monthlyPI)}</div>
              </div>

              <table style={styles.summaryTable}>
                <tbody>
                  <tr style={styles.summaryRow}>
                    <td style={styles.summaryCell}>{t("principalInterestLabel")}</td>
                    <td style={styles.summaryCellValue}>{formatCurrency(results.monthlyPI)}</td>
                  </tr>
                  {results.includeOptional && results.propertyTaxMonthly > 0 && (
                    <tr style={styles.summaryRow}>
                      <td style={styles.summaryCell}>{t("propertyTaxLabel")}</td>
                      <td style={styles.summaryCellValue}>{formatCurrency(results.propertyTaxMonthly)}</td>
                    </tr>
                  )}
                  {results.includeOptional && results.homeInsMonthly > 0 && (
                    <tr style={styles.summaryRow}>
                      <td style={styles.summaryCell}>{t("homeInsuranceLabel")}</td>
                      <td style={styles.summaryCellValue}>{formatCurrency(results.homeInsMonthly)}</td>
                    </tr>
                  )}
                  {results.includeOptional && results.pmiMonthly > 0 && (
                    <tr style={styles.summaryRow}>
                      <td style={styles.summaryCell}>{t("pmiLabel")}</td>
                      <td style={styles.summaryCellValue}>{formatCurrency(results.pmiMonthly)}</td>
                    </tr>
                  )}
                  {results.includeOptional && results.hoaMonthly > 0 && (
                    <tr style={styles.summaryRow}>
                      <td style={styles.summaryCell}>{t("hoaFeeLabel")}</td>
                      <td style={styles.summaryCellValue}>{formatCurrency(results.hoaMonthly)}</td>
                    </tr>
                  )}
                  {results.includeOptional && results.otherMonthly > 0 && (
                    <tr style={styles.summaryRow}>
                      <td style={styles.summaryCell}>{t("otherCostsLabel")}</td>
                      <td style={styles.summaryCellValue}>{formatCurrency(results.otherMonthly)}</td>
                    </tr>
                  )}
                  <tr style={styles.highlightRow}>
                    <td style={styles.highlightCell}>{t("totalMonthlyPaymentLabel")}</td>
                    <td style={{ ...styles.highlightCell, textAlign: 'right' }}>{formatCurrency(results.totalMonthlyPayment)}</td>
                  </tr>
                </tbody>
              </table>

              <table style={{ ...styles.summaryTable, marginTop: '1.5rem' }}>
                <tbody>
                  <tr style={styles.summaryRow}>
                    <td style={styles.summaryCell}>{t("homePriceLabel")}</td>
                    <td style={styles.summaryCellValue}>{formatCurrency(results.homePrice)}</td>
                  </tr>
                  <tr style={styles.summaryRow}>
                    <td style={styles.summaryCell}>{t("loanAmountLabel")}</td>
                    <td style={styles.summaryCellValue}>{formatCurrency(results.loanAmount)}</td>
                  </tr>
                  <tr style={styles.summaryRow}>
                    <td style={styles.summaryCell}>{t("downPaymentLabel")}</td>
                    <td style={styles.summaryCellValue}>{formatCurrency(results.downPaymentAmount)}</td>
                  </tr>
                  <tr style={styles.summaryRow}>
                    <td style={styles.summaryCell}>{t("totalInterestLabel")}</td>
                    <td style={styles.summaryCellValue}>{formatCurrency(results.totalInterest)}</td>
                  </tr>
                  <tr style={styles.summaryRow}>
                    <td style={styles.summaryCell}>{t("totalPaymentsLabel")}</td>
                    <td style={styles.summaryCellValue}>{formatCurrency(results.totalPayments)}</td>
                  </tr>
                  <tr style={styles.highlightRow}>
                    <td style={styles.highlightCell}>{t("payoffDateLabel")}</td>
                    <td style={{ ...styles.highlightCell, textAlign: 'right' }}>{results.payoffDate}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {schedule.length > 0 && (
          <div style={styles.amortizationSchedule}>
            <h3 style={styles.scheduleTitle}>{t("amortizationScheduleTitle")}</h3>
            <div style={styles.scheduleTableWrapper}>
              <table style={styles.scheduleTable}>
                <thead style={styles.scheduleHeader}>
                  <tr>
                    <th style={styles.scheduleTh}>{t("paymentDateColumn")}</th>
                    <th style={{ ...styles.scheduleTh, textAlign: 'right' }}>{t("paymentColumn")}</th>
                    <th style={{ ...styles.scheduleTh, textAlign: 'right' }}>{t("principalColumn")}</th>
                    <th style={{ ...styles.scheduleTh, textAlign: 'right' }}>{t("interestColumn")}</th>
                    <th style={{ ...styles.scheduleTh, textAlign: 'right' }}>{t("balanceColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((payment, index) => {
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const dateStr = monthNames[payment.date.getMonth()] + ' ' + payment.date.getFullYear();

                    return (
                      <tr key={index} style={styles.scheduleRow}>
                        <td style={styles.scheduleTd}>{dateStr}</td>
                        <td style={{ ...styles.scheduleTd, textAlign: 'right' }}>{formatCurrency(payment.payment)}</td>
                        <td style={{ ...styles.scheduleTd, textAlign: 'right' }}>{formatCurrency(payment.principal)}</td>
                        <td style={{ ...styles.scheduleTd, textAlign: 'right' }}>{formatCurrency(payment.interest)}</td>
                        <td style={{ ...styles.scheduleTd, textAlign: 'right' }}>{formatCurrency(payment.balance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToUseStep1")}</li>
          <li style={styles.listItem}>{t("howToUseStep2")}</li>
          <li style={styles.listItem}>{t("howToUseStep3")}</li>
          <li style={styles.listItem}>{t("howToUseStep4")}</li>
          <li style={styles.listItem}>{t("howToUseStep5")}</li>
          <li style={styles.listItem}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <p style={styles.paragraph}>{t("insightsParagraph1")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insightsBullet1")}</li>
          <li style={styles.listItem}>{t("insightsBullet2")}</li>
          <li style={styles.listItem}>{t("insightsBullet3")}</li>
          <li style={styles.listItem}>{t("insightsBullet4")}</li>
          <li style={styles.listItem}>{t("insightsBullet5")}</li>
        </ul>
        <p style={styles.paragraph}>{t("insightsParagraph2")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <p style={styles.paragraph}>{t("detailsParagraph1")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph2")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph3")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph4")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion1")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer1")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion2")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer2")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion3")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer3")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion4")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer4")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion5")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer5")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.referenceList}>
          <li style={styles.referenceItem}>
            <a
              href="https://www.triadfs.com/news/how-to-use-an-amortization-calculator-for-your-home-loan"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference1")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a
              href="https://www.nerdwallet.com/mortgages/learn/what-is-mortgage-amortization"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference2")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a
              href="https://fhmtg.com/2019/01/22/what-is-a-mortgage-amortization-schedule/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference3")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a
              href="https://www.ent.com/education-center/smart-money-management/benefits-of-using-an-amortization-schedule-for-loan-repayment/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference4")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a
              href="https://www.rocketmortgage.com/learn/amortization-real-estate"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference5")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a
              href="https://myhome.freddiemac.com/owning/understanding-amortization"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference6")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a
              href="https://www.chase.com/personal/mortgage/education/financing-a-home/loan-amortization"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference7")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}